import React from 'react';
import ComparisonCharts from './ComparisonCharts';
import SurfaceStatsDetail from './SurfaceStatsDetail';
import WinGauge from './WinGauge';

function SurfaceTabs({ 
  selected1, 
  selected2, 
  comparisonResults,
  renderConfidence,
  getAiSummary,
  setAiSummary
}) {
  const [activeTab, setActiveTab] = React.useState('global');
  
  // Vérifier la validité de l'onglet actif et réinitialiser à global si nécessaire
  React.useEffect(() => {
    if (!selected1 || !selected2 || !comparisonResults) return;
    
    // Define isValidSurface inside useEffect to access comparisonResults
    const isValidSurface = (surface) => {
      return comparisonResults && 
             comparisonResults[surface] && 
             comparisonResults[surface].advanced &&
             typeof comparisonResults[surface].advanced.player1 === 'number' &&
             typeof comparisonResults[surface].advanced.player2 === 'number';
    };
    
    const validSurfaces = {
      hard: isValidSurface('hard'),
      clay: isValidSurface('clay'),
      grass: isValidSurface('grass')
    };
    
    if (activeTab !== 'global' && !validSurfaces[activeTab]) {
      // Si l'onglet actif n'a pas de données, revenir à l'onglet global
      setActiveTab('global');
    }
  }, [activeTab, comparisonResults, selected1, selected2]);
  
  if (!selected1 || !selected2 || !comparisonResults) {
    return null;
  }
  
  // Map surface codes to display names, icons and colors
  const surfaceData = {
    global: {
      name: 'Global',
      color: '#3498db',
      icon: '🌍'
    },
    hard: {
      name: 'Dur',
      color: '#3498db',
      icon: '🔵'
    },
    clay: {
      name: 'Terre Battue',
      color: '#e67e22',
      icon: '🟠'
    },
    grass: {
      name: 'Gazon',
      color: '#2ecc71',
      icon: '🟢'
    }
  };
  
  // Amélioration des vérifications pour éviter les erreurs
  const isValidSurface = (surface) => {
    return comparisonResults && 
           comparisonResults[surface] && 
           comparisonResults[surface].advanced &&
           typeof comparisonResults[surface].advanced.player1 === 'number' &&
           typeof comparisonResults[surface].advanced.player2 === 'number';
  };
  
  // Vérifier si toutes les données nécessaires sont disponibles pour chaque surface
  const hasSurfaceData = {
    hard: isValidSurface('hard'),
    clay: isValidSurface('clay'),
    grass: isValidSurface('grass')
  };
  
  // Determine the leading player for each surface
  const getLeadingPlayer = (surface) => {
    if (surface === 'global') {
      // Calculate average probabilities across all surfaces
      let totalAvailable = 0;
      let player1Sum = 0;
      let player2Sum = 0;
      
      if (hasSurfaceData.hard) {
        player1Sum += comparisonResults.hard.advanced.player1;
        player2Sum += comparisonResults.hard.advanced.player2;
        totalAvailable++;
      }
      
      if (hasSurfaceData.clay) {
        player1Sum += comparisonResults.clay.advanced.player1;
        player2Sum += comparisonResults.clay.advanced.player2;
        totalAvailable++;
      }
      
      if (hasSurfaceData.grass) {
        player1Sum += comparisonResults.grass.advanced.player1;
        player2Sum += comparisonResults.grass.advanced.player2;
        totalAvailable++;
      }
      
      // Si aucune donnée n'est disponible, retourner des valeurs par défaut
      if (totalAvailable === 0) {
        return { player: selected1, winProb: 50 };
      }
      
      const player1Avg = player1Sum / totalAvailable;
      const player2Avg = player2Sum / totalAvailable;
      
      return player1Avg > player2Avg 
        ? { player: selected1, winProb: Math.round(player1Avg) }
        : { player: selected2, winProb: Math.round(player2Avg) };
    }
    
    // Si les données pour cette surface ne sont pas disponibles
    if (!hasSurfaceData[surface]) {
      return { player: selected1, winProb: 50 };
    }
    
    // Utiliser les probabilités avancées spécifiques à la surface (au lieu de 50/50)
    const player1Prob = comparisonResults[surface].advanced.player1;
    const player2Prob = comparisonResults[surface].advanced.player2;
    
    return player1Prob > player2Prob
      ? { player: selected1, winProb: player1Prob }
      : { player: selected2, winProb: player2Prob };
  };
  
  return (
    <div style={{ marginTop: 30 }}>
      {/* Tab navigation */}
      <div style={{
        display: 'flex',
        backgroundColor: '#f5f5f5',
        borderRadius: '8px',
        padding: '4px',
        marginBottom: '24px',
        borderBottom: '1px solid #eee',
        overflowX: 'auto'
      }}>
        {Object.entries(surfaceData).map(([key, data]) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '10px 16px',
              background: activeTab === key ? '#fff' : 'transparent',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: activeTab === key ? 'bold' : 'normal',
              color: activeTab === key ? data.color : '#666',
              position: 'relative',
              overflow: 'hidden',
              boxShadow: activeTab === key ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
              transition: 'all 0.2s ease',
              margin: '0 2px',
              flex: '1',
              minWidth: 'fit-content'
            }}
          >
            <span style={{ marginRight: '8px' }}>{data.icon}</span>
            {data.name}
            {activeTab === key && (
              <span style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: '2px',
                background: data.color
              }}></span>
            )}
          </button>
        ))}
      </div>
      
      {/* Global tab content */}
      {activeTab === 'global' && (
        <div className="tab-content">
          <div style={{
            marginBottom: 30,
            padding: 20,
            backgroundColor: '#fff',
            borderRadius: 8,
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            border: '1px solid #eee'
          }}>
            <h4 style={{ marginTop: 0, marginBottom: 15, textAlign: 'center' }}>Prédiction Globale</h4>
            
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {/* Gauge showing global prediction */}
              <div style={{ marginBottom: 15 }}>
                <WinGauge 
                  value={getLeadingPlayer('global').winProb} 
                  primary={surfaceData.global.color}
                  size={130}
                />
              </div>
              
              <h5 style={{ margin: '10px 0' }}>
                {getLeadingPlayer('global').player.player} est favorisé sur l'ensemble des surfaces
              </h5>
              
              <div style={{ 
                fontSize: 14, 
                color: '#666', 
                textAlign: 'center',
                maxWidth: '450px',
                marginTop: 5
              }}>
                Cette prédiction est basée sur une moyenne des performances attendues sur toutes les surfaces.
              </div>
            </div>
          </div>
          
          <ComparisonCharts 
            selected1={selected1}
            selected2={selected2}
            comparisonResults={comparisonResults}
          />
          
          <div style={{
            marginTop: 30,
            padding: 20,
            border: '1px solid #eee',
            borderRadius: 8,
            backgroundColor: '#f9f9f9'
          }}>
            <h4 style={{ marginTop: 0, marginBottom: 15 }}>Comparaison globale</h4>
            
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20 }}>
              <div style={{ flex: '1 1 300px' }}>
                <h5 style={{ marginBottom: 10 }}>{selected1.player}</h5>
                <ul style={{ listStyle: 'none', padding: 0 }}>
                  <li style={{ marginBottom: '8px' }}>
                    <strong>Elo général:</strong> {selected1.elo}
                  </li>
                  <li style={{ marginBottom: '8px' }}>
                    <strong>Elo dur:</strong> {selected1.helo}
                  </li>
                  <li style={{ marginBottom: '8px' }}>
                    <strong>Elo terre battue:</strong> {selected1.celo}
                  </li>
                  <li style={{ marginBottom: '8px' }}>
                    <strong>Elo gazon:</strong> {selected1.gelo}
                  </li>
                  <li style={{ marginBottom: '8px' }}>
                    <strong>Classement Elo:</strong> {selected1.elo_rank}
                  </li>
                  <li style={{ marginBottom: '8px' }}>
                    <strong>Classement ATP:</strong> {selected1.atp_rank}
                  </li>
                </ul>
              </div>
              
              <div style={{ flex: '1 1 300px' }}>
                <h5 style={{ marginBottom: 10 }}>{selected2.player}</h5>
                <ul style={{ listStyle: 'none', padding: 0 }}>
                  <li style={{ marginBottom: '8px' }}>
                    <strong>Elo général:</strong> {selected2.elo}
                  </li>
                  <li style={{ marginBottom: '8px' }}>
                    <strong>Elo dur:</strong> {selected2.helo}
                  </li>
                  <li style={{ marginBottom: '8px' }}>
                    <strong>Elo terre battue:</strong> {selected2.celo}
                  </li>
                  <li style={{ marginBottom: '8px' }}>
                    <strong>Elo gazon:</strong> {selected2.gelo}
                  </li>
                  <li style={{ marginBottom: '8px' }}>
                    <strong>Classement Elo:</strong> {selected2.elo_rank}
                  </li>
                  <li style={{ marginBottom: '8px' }}>
                    <strong>Classement ATP:</strong> {selected2.atp_rank}
                  </li>
                </ul>
              </div>
            </div>
            
            <div style={{ marginTop: 20 }}>
              <h4 style={{ marginBottom: 15 }}>Probabilités de victoire</h4>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20 }}>
                {hasSurfaceData.hard && (
                  <div style={{ 
                    flex: '1 1 200px', 
                    padding: '16px', 
                    backgroundColor: '#fff', 
                    borderRadius: '8px', 
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)' 
                  }}>
                    <h5 style={{ textAlign: 'center', marginTop: 0 }}>Dur</h5>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <div style={{ textAlign: 'center', flex: 1 }}>
                        <div style={{ 
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '4px'
                        }}>
                          <WinGauge 
                            value={comparisonResults.hard.advanced.player1} 
                            primary={surfaceData.hard.color}
                            size={45}
                            showValue={false}
                          />
                          <div style={{ fontSize: '14px', fontWeight: 'bold' }}>
                            {comparisonResults.hard.advanced.player1}%
                          </div>
                          <div style={{ fontSize: '12px' }}>{selected1.player}</div>
                        </div>
                      </div>
                      <div style={{ textAlign: 'center', flex: 1 }}>
                        <div style={{ 
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '4px'
                        }}>
                          <WinGauge 
                            value={comparisonResults.hard.advanced.player2} 
                            primary={surfaceData.hard.color}
                            size={45}
                            showValue={false}
                          />
                          <div style={{ fontSize: '14px', fontWeight: 'bold' }}>
                            {comparisonResults.hard.advanced.player2}%
                          </div>
                          <div style={{ fontSize: '12px' }}>{selected2.player}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {hasSurfaceData.clay && (
                  <div style={{ 
                    flex: '1 1 200px', 
                    padding: '16px', 
                    backgroundColor: '#fff', 
                    borderRadius: '8px', 
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)' 
                  }}>
                    <h5 style={{ textAlign: 'center', marginTop: 0 }}>Terre Battue</h5>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <div style={{ textAlign: 'center', flex: 1 }}>
                        <div style={{ 
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '4px'
                        }}>
                          <WinGauge 
                            value={comparisonResults.clay.advanced.player1} 
                            primary={surfaceData.clay.color}
                            size={45}
                            showValue={false}
                          />
                          <div style={{ fontSize: '14px', fontWeight: 'bold' }}>
                            {comparisonResults.clay.advanced.player1}%
                          </div>
                          <div style={{ fontSize: '12px' }}>{selected1.player}</div>
                        </div>
                      </div>
                      <div style={{ textAlign: 'center', flex: 1 }}>
                        <div style={{ 
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '4px'
                        }}>
                          <WinGauge 
                            value={comparisonResults.clay.advanced.player2} 
                            primary={surfaceData.clay.color}
                            size={45}
                            showValue={false}
                          />
                          <div style={{ fontSize: '14px', fontWeight: 'bold' }}>
                            {comparisonResults.clay.advanced.player2}%
                          </div>
                          <div style={{ fontSize: '12px' }}>{selected2.player}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {hasSurfaceData.grass && (
                  <div style={{ 
                    flex: '1 1 200px', 
                    padding: '16px', 
                    backgroundColor: '#fff', 
                    borderRadius: '8px', 
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)' 
                  }}>
                    <h5 style={{ textAlign: 'center', marginTop: 0 }}>Gazon</h5>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <div style={{ textAlign: 'center', flex: 1 }}>
                        <div style={{ 
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '4px'
                        }}>
                          <WinGauge 
                            value={comparisonResults.grass.advanced.player1} 
                            primary={surfaceData.grass.color}
                            size={45}
                            showValue={false}
                          />
                          <div style={{ fontSize: '14px', fontWeight: 'bold' }}>
                            {comparisonResults.grass.advanced.player1}%
                          </div>
                          <div style={{ fontSize: '12px' }}>{selected1.player}</div>
                        </div>
                      </div>
                      <div style={{ textAlign: 'center', flex: 1 }}>
                        <div style={{ 
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '4px'
                        }}>
                          <WinGauge 
                            value={comparisonResults.grass.advanced.player2} 
                            primary={surfaceData.grass.color}
                            size={45}
                            showValue={false}
                          />
                          <div style={{ fontSize: '14px', fontWeight: 'bold' }}>
                            {comparisonResults.grass.advanced.player2}%
                          </div>
                          <div style={{ fontSize: '12px' }}>{selected2.player}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {!hasSurfaceData.hard && !hasSurfaceData.clay && !hasSurfaceData.grass && (
                  <div style={{ width: '100%', textAlign: 'center', padding: '20px' }}>
                    <p>Aucune donnée détaillée disponible pour ce match.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Surface-specific tabs content */}
      {activeTab !== 'global' && hasSurfaceData[activeTab] && 
       comparisonResults[activeTab] && comparisonResults[activeTab].advanced && (
        <div className="tab-content">
          {/* Surface prediction gauge */}
          <div style={{
            marginBottom: 30,
            padding: 20,
            backgroundColor: '#fff',
            borderRadius: 8,
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            border: '1px solid #eee'
          }}>
            <h4 style={{ marginTop: 0, marginBottom: 15, textAlign: 'center' }}>
              Prédiction sur {surfaceData[activeTab].name}
            </h4>
            
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {/* Gauge showing surface prediction */}
              <div style={{ marginBottom: 15 }}>
                <WinGauge 
                  value={getLeadingPlayer(activeTab).winProb} 
                  primary={surfaceData[activeTab].color}
                  size={130}
                />
              </div>
              
              <h5 style={{ margin: '10px 0' }}>
                {getLeadingPlayer(activeTab).player.player} est favorisé sur {surfaceData[activeTab].name.toLowerCase()}
              </h5>
              
              <div style={{ 
                fontSize: 14, 
                color: '#666', 
                textAlign: 'center',
                maxWidth: '450px',
                marginTop: 5
              }}>
                Cette prédiction est basée sur les statistiques et performances spécifiques à la surface.
              </div>
            </div>
          </div>
          
          <SurfaceStatsDetail
            surface={surfaceData[activeTab].name}
            comparison={comparisonResults[activeTab]}
            player1={selected1}
            player2={selected2}
            getAiSummary={getAiSummary}
            setAiSummary={setAiSummary}
          />
        </div>
      )}
      
      {/* Message d'erreur si les données de la surface ne sont pas disponibles */}
      {activeTab !== 'global' && !hasSurfaceData[activeTab] && (
        <div className="tab-content" style={{
          padding: 20,
          backgroundColor: '#fff',
          borderRadius: 8,
          textAlign: 'center',
          border: '1px solid #eee'
        }}>
          <h4 style={{ marginTop: 0, marginBottom: 15 }}>
            Données non disponibles
          </h4>
          <p>
            Les statistiques détaillées pour {surfaceData[activeTab].name.toLowerCase()} ne sont pas disponibles pour ce match.
          </p>
        </div>
      )}
    </div>
  );
}

export default SurfaceTabs; 